import { CreateLocationRequest, CreatePopUpRequest, FeatureType, GetPopUpResponse, ObjectId, PopUpAction, PopUpTrigger } from '../types';
import { LitElement, css, html } from 'lit';
import { createPopup, updatePopup } from '../helpers/api/popups-api';
import { customElement, property, query, state } from 'lit/decorators.js';
import { isDefined, isValidString, undefinedIfEmpty } from '../helpers/utils';

import { DateTimeRangePickerComponent } from './common/date-time-range-picker';
import { EmailCaptureModal } from './email-capture-modal';
import { StateController } from '../helpers/state/state-management/state-controller';
import { getMainPage } from '../helpers/page-name';
import { convertUTCToLocale } from '../helpers/date';
import { format } from 'date-fns';
import { ifDefined } from 'lit/directives/if-defined';
import pointCache from '../helpers/state/point-cache';
import { timezoneAsync } from '../helpers/api/google-api';
import topicData from '../helpers/state/topic-data';

@customElement('create-pop-up-component')
export class CreatePopUpComponent extends LitElement {

	@property({ type: String })
	organizationId: string = '';

	@property({ type: String })
	organizationKey?: string;

	@property()
	private popup_type: PopUpTrigger = PopUpTrigger.OnMapLoad;

	@property()
	private popup_target_id?: ObjectId; // usually topicid

	@state()
    private timezoneId: string = '';

    @state()
    private timezoneShortName: string = '';

	@state()
    private currentTabIndex: number = 0;

	private static readonly TAB_OPTIONS = ['Pop Ups'] as const;

	private get selectedTab(): typeof CreatePopUpComponent.TAB_OPTIONS[number] {
		return CreatePopUpComponent.TAB_OPTIONS[this.currentTabIndex];
	}

	@state()
	private selectedActionType: string = 'None';

	@state()
	private errors: Record<string, string> = {};

	@state()
	private reloading: boolean = false;

	@state()
	private existing_popup?: GetPopUpResponse;

	@state()
    private previewMode: boolean = false;

	@state()
	popup?: CreatePopUpRequest;

	@state()
	private readonly recurrence: 'once' | 'daily' | 'weekly' | 'monthly' = 'once';

	@state()
	private includeOptIn: boolean = false;

	@property()
    featureEnabled: boolean = false;

	@query('#dateTimeRangePicker')
	private readonly dateTimeRangePicker?: DateTimeRangePickerComponent;

	@query('#emailCapturePreview')
	private readonly emailCaptureModal?: EmailCaptureModal;

	private readonly bindPointState = new StateController(this, pointCache);

	public async setExistingPopUp(popup: GetPopUpResponse) {
		this.reloading = true;
		this.popup = undefined;
		this.existing_popup = popup;
		this.popup = {
			_id : popup._id,
			organization_id: popup.organization_id,
			name: popup.name,
			pop_up_type: popup.pop_up_type,
			action: popup.action,
			is_active: popup.is_active,
			header: popup.header,
			description: popup.description,
			background_color: popup.background_color,
			text_color: popup.text_color,
			cta_background_color: popup.cta_background_color,
			cta_text_color: popup.cta_text_color,
			display_when_embedded: popup.display_when_embedded,
			display_delay_seconds: popup.display_delay_seconds,
			active_from: popup.active_from ?? null,
			active_until: popup.active_until ?? null,
			geo_trigger: popup.geo_trigger ?? null,
			priority: popup.priority,
			recurrence: popup.recurrence,
			topic_ids: popup.topic_ids,
			header_logo_url: popup.header_logo_url,
			show_distance_to_user: popup.show_distance_to_user ?? false,
			show_point_actions: popup.show_point_actions ?? false,
			show_rating_field_id: popup.show_rating_field_id,
		};

		// Set the action type selector
		this.selectedActionType = this.getActionType(popup.action);

		// Initialize includeOptIn based on whether opt_in_language exists
		if (popup.action.type === 'collect-email' && isDefined(undefinedIfEmpty(popup.action.data.opt_in_language))) {
			this.includeOptIn = true;
		} else {
			this.includeOptIn = false;
		}

		this.reloading = false;

		// Ensure timezone is loaded, then set the date-time picker values
		await this.ensureTimezoneId();
		await this.updateComplete;

		if (!isDefined(this.dateTimeRangePicker)) {
			return;
		}

		if (isDefined(popup.active_from)) {
			const startDateTime = convertUTCToLocale(popup.active_from, this.timezoneId);
			(this.dateTimeRangePicker.startDateElement.value as any) = format(startDateTime, 'yyyy-MM-dd');
			(this.dateTimeRangePicker.startTimeElement.value as any) = format(startDateTime, 'HH:mm');
		}

		if (isDefined(popup.active_until)) {
			const endDateTime = convertUTCToLocale(popup.active_until, this.timezoneId);
			(this.dateTimeRangePicker.endDateElement.value as any) = format(endDateTime, 'yyyy-MM-dd');
			(this.dateTimeRangePicker.endTimeElement.value as any) = format(endDateTime, 'HH:mm');
		}

		// Clear the picker if no time logic is set
		if (!isDefined(popup.active_from) && !isDefined(popup.active_until)) {
			this.dateTimeRangePicker.clearAll();
		}
	}

	private static readonly ACTION_TYPE_MAP: Record<PopUpAction['type'], string> = {
		'none': 'None',
		'collect-email': 'CollectEmail',
		'link-out': 'LinkOut',
		'open-point': 'OpenPoint',
	};

	private getActionType(action: PopUpAction): string {
		return CreatePopUpComponent.ACTION_TYPE_MAP[action.type] ?? 'None';
	}

	private getDraftButtonLabel(): string {
		return (this.existing_popup?.is_active === true) ? 'Archive & Move to Drafts' : 'Save as Draft';
	}

	private getLiveButtonLabel(): string {
		return (this.existing_popup?.is_active === true) ? 'Save Changes' : 'Save and Set Live';
	}

	private validateForm(): boolean {
		this.errors = {};

		if (!isDefined(undefinedIfEmpty(this.popup?.name?.trim()))) {
			this.errors.name = 'Name is required';
		}
		if (!isDefined(undefinedIfEmpty(this.popup?.header?.trim()))) {
			this.errors.header = 'Header is required';
		}
		if (!isDefined(undefinedIfEmpty(this.popup?.description?.trim()))) {
			this.errors.description = 'Description is required';
		}
		if (this.popup?.topic_ids.length === 0) {
			this.errors.topics = 'Select at least one topic';
		}

		return Object.keys(this.errors).length === 0;
	}

	private async handleSave(isActive: boolean) {
		if (!this.validateForm()) {
			return;
		}

		if (!isDefined(this.popup)) {
			return;
		}

		// Set the is_active status before saving
		this.popup.is_active = isActive;

		try {
			const callsite = 'create-pop-up-component';
			if (isDefined(this.existing_popup)) {
				await updatePopup(callsite, this.organizationId, this.existing_popup?._id.$oid ?? '', this.popup, this.organizationKey);
			} else {
				await createPopup(callsite, this.organizationId, this.popup, this.organizationKey);
			}
			this.dispatchEvent(new CustomEvent('popup-changed', { detail: { popup: this.popup } }));
			this.handleClose();
		} catch (e) {
			getMainPage().errorToastElement.showToast('Failed to save popup. Please try again.');
			console.error('Popup save error:', e);
		}
	}

	private async handleSaveAsDraft() {
		await this.handleSave(false);
	}

	private async handleSaveAndSetLive() {
		await this.handleSave(true);
	}

	private handleClose() {
		this.popup = undefined;
		this.selectedActionType = 'None';
		this.errors = {};
		this.includeOptIn = false;
		this.dispatchEvent(new CustomEvent('close'));
	}

	private handleActionTypeChange(type: string) {
		if (!isDefined(this.popup)) return;
		this.selectedActionType = type;
		switch (type) {
			case 'None':
				this.popup.action = { type: 'none' };
				break;
			case 'CollectEmail':
				this.popup.action = { type: 'collect-email', data: { opt_in_language: '' } };
			this.includeOptIn = false;
			break;
			case 'LinkOut':
				this.popup.action = { type: 'link-out', data: { url: '', button_text: '' } };
				break;
			case 'OpenPoint':
				this.popup.action = { type: 'open-point', data: { point_id: { $oid: '' }, button_text: '' } };
				break;
		}
		this.requestUpdate();
		this.dispatchEvent(new CustomEvent('popup-updated', { detail: { popup: this.popup } }));
	}

	private updatePopupScheduledTime() {
		if (!isDefined(this.popup) || (!isDefined(this.popup.active_from) && !isDefined(this.popup.active_until))) {
			return;
		}
		if (!isDefined(this.dateTimeRangePicker?.startDateElement?.value)) {
			return;
		}

		if (!isDefined(this.dateTimeRangePicker)) {
			return;
		}

		const startDate = this.dateTimeRangePicker.startDateElement.value;
		const startTime = this.dateTimeRangePicker.startTimeElement.value;
		const endDate = this.dateTimeRangePicker.endDateElement?.value;
		const endTime = this.dateTimeRangePicker.endTimeElement?.value;

		if (startDate && startTime) {
			const startDateTime = this.convertLocalTimeToUTC(startDate, startTime);
			this.setPopUpSetting({ active_from: startDateTime });
		}

		if (endDate && endTime) {
			const endDateTime = this.convertLocalTimeToUTC(endDate, endTime);
			this.setPopUpSetting({ active_until: endDateTime });
		}
	}

	private convertLocalTimeToUTC(dateStr: string, timeStr: string): string {
		// Convert user's local time (in their timezone) to UTC ISO string
		// This accounts for the timezone offset of this.timezoneId
		const localDateTime = new Date(`${dateStr}T${timeStr}:00.000`);

		// Get UTC time by adjusting for the browser's local offset
		// Note: This is a simplified approach that assumes the timezone hasn't changed
		// For more precision, use a timezone library like date-fns-tz
		return localDateTime.toISOString();
	}

	private renderActionFields() {
		if (!isDefined(this.popup)) return html``;
		switch (this.selectedActionType) {
			case 'CollectEmail':
				return this.renderCollectEmailFields();
			case 'LinkOut':
				return this.renderLinkOutFields();
			case 'OpenPoint':
				return this.renderOpenPointFields();
			default:
				return html``;
		}
	}

	private renderCollectEmailFields() {
		return html`
			<div class="form-group">
				<label style="display: flex; align-items: center; gap: 8px;">
					<input
						type="checkbox"
						?checked=${this.includeOptIn}
						@change=${(e: Event) => {
							const target = e.target as HTMLInputElement;
							this.includeOptIn = target.checked;
							if (!target.checked && this.popup?.action.type === 'collect-email') {
								this.popup.action.data.opt_in_language = '';
								this.dispatchEvent(new CustomEvent('popup-updated', { detail: { popup: this.popup } }));
							}
							this.requestUpdate();
						}}
					>
					Include Opt In
				</label>
				${this.includeOptIn ? html`
					<proxi-textarea
						outlined
						.value=${this.popup?.action.type === 'collect-email' ? (this.popup.action.data.opt_in_language ?? '') : ''}
						@input=${(e: Event) => {
							const target = e.target as HTMLTextAreaElement;
							if (this.popup?.action.type === 'collect-email') {
								this.popup.action.data.opt_in_language = target.value;
								this.dispatchEvent(new CustomEvent('popup-updated', { detail: { popup: this.popup } }));
								this.requestUpdate();
							}
						}}
					></proxi-textarea>
				` : ''}
			</div>
		`;
	}

	private renderLinkOutFields() {
		return html`
			<div class="form-group">
				<label>Link URL</label>
				<input
					type="text"
					.value=${this.popup?.action.type === 'link-out' ? (this.popup.action.data.url ?? '') : ''}
					@input=${(e: Event) => {
						const target = e.target as HTMLInputElement;
						if (this.popup?.action.type === 'link-out') {
							this.popup.action.data.url = target.value;
							this.dispatchEvent(new CustomEvent('popup-updated', { detail: { popup: this.popup } }));
							this.requestUpdate();
						}
					}}
					placeholder="https://example.com"
				/>
			</div>
			<div class="form-group">
				<label>Button Text</label>
				<input
					type="text"
					.value=${this.popup?.action.type === 'link-out' ? (this.popup.action.data.button_text ?? '') : ''}
					@input=${(e: Event) => {
						const target = e.target as HTMLInputElement;
						if (this.popup?.action.type === 'link-out') {
							this.popup.action.data.button_text = target.value;
							this.dispatchEvent(new CustomEvent('popup-updated', { detail: { popup: this.popup } }));
							this.requestUpdate();
						}
					}}
					placeholder="Click here"
				/>
			</div>
		`;
	}

	private renderOpenPointFields() {
		return html`
			<div class="form-group">
				<label>Point</label>
				<proxi-selectfield
					outlined
					.height="${'1.5rem'}"
					value=${this.popup?.action.type === 'open-point' ? (this.popup.action.data.point_id.$oid ?? '') : ''}
					@selected=${(e: Event) => {
						const target = e.target as HTMLInputElement;
						if (this.popup?.action.type === 'open-point') {
							this.popup.action.data.point_id = { $oid: target.value };
							this.dispatchEvent(new CustomEvent('popup-updated', { detail: { popup: this.popup } }));
							// Auto-populate geofence center from selected point's location
							if (isDefined(this.popup?.geo_trigger)) {
								const selectedPoint = pointCache.orderedPoints.find(p => p._id?.$oid === target.value);
								if (selectedPoint?.location?.latLng) {
									this.onGeofenceInputChanged({
										lat: selectedPoint.location.latLng.lat,
										lng: selectedPoint.location.latLng.lng,
									});
								}
							}
							this.requestUpdate();
						}
					}}
				>
					${pointCache.orderedPoints.map((point) => html`
						<mwc-list-item value="${point._id?.$oid || ''}">${point.name}</mwc-list-item>
					`)}
				</proxi-selectfield>
			</div>
			<div class="form-group">
				<label>Button Text</label>
				<input
					type="text"
					.value=${this.popup?.action.type === 'open-point' ? (this.popup.action.data.button_text ?? '') : ''}
					@input=${(e: Event) => {
						const target = e.target as HTMLInputElement;
						if (this.popup?.action.type === 'open-point') {
							this.popup.action.data.button_text = target.value;
							this.dispatchEvent(new CustomEvent('popup-updated', { detail: { popup: this.popup } }));
							this.requestUpdate();
						}
					}}
					placeholder="View location"
				/>
			</div>
			<div class="checkbox-wrapper" style="margin-top: 8px;">
				<form-checkbox
					id="showPointActions"
					name="showPointActions"
					class_name="blue"
					?checked="${this.popup?.show_point_actions}"
					@change="${(e: Event) => {
						const target = e.target as HTMLInputElement;
						this.setPopUpSetting({ show_point_actions: target.checked });
					}}"
				></form-checkbox>
				<span>Show point actions (directions, website, etc.)</span>
			</div>
			${(topicData.topic?.add_point_customization?.custom_fields ?? []).filter(f => !f.archived && 'Rating' in f.field_type).length > 0 ?
				html`
					<div class="form-group" style="margin-top: 8px;">
						<label>Show Rating</label>
						<proxi-selectfield
							outlined
							.height="${'1.5rem'}"
							value=${this.popup?.show_rating_field_id?.$oid ?? ''}
							@selected=${(e: Event) => {
								const target = e.target as HTMLInputElement;
								const value = target.value;
								this.setPopUpSetting({
									show_rating_field_id: value ? { $oid: value } : undefined,
								});
							}}
						>
							<mwc-list-item value="">None</mwc-list-item>
							${(topicData.topic?.add_point_customization?.custom_fields ?? []).filter(f => !f.archived && 'Rating' in f.field_type).map(f => html`
								<mwc-list-item value="${f._id?.$oid ?? ''}">${f.customizations.header ?? 'Rating'}</mwc-list-item>
							`)}
						</proxi-selectfield>
					</div>
				` : ''
			}
		`;
	}

	openUpgradeDrawer() {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: FeatureType.LeadsAcrossMaps,
                }
            })
        );
    }

    handleChangeTab(e: CustomEvent) {
        this.currentTabIndex = e.detail.tabIndex;
        this.requestUpdate();
    }


    renderReloading() {
        return html`
            <loading-element
            .loadingString=${'Loading Pop Up...'}
            .horizontal=${true}></loading-element>`;
    }

	renderCreate() {
		return html`
			<div class="modal__header" @click="${async () => await this.createPopUp(this.popup_target_id, this.popup_type)}">
				<form-button variant="outline" size="sm" ?fullWidth=${true}>Create Pop Up</form-button>
			</div>
			`
		;
    }

	private async ensureTimezoneId() {
        if (isValidString(this.timezoneId)) {
            return;
        }

        const timezoneInfos = await timezoneAsync(String(topicData.topic?.default_map_center.latLng.lat), String(topicData.topic?.default_map_center.latLng.lng));
        this.timezoneId = timezoneInfos[0]?.identifier;
        this.timezoneShortName = timezoneInfos[0]?.shortIdentifier;
    }

	 async createPopUp(entity_id: ObjectId | undefined, entity_type: PopUpTrigger) {
		this.reloading = true;
		await this.ensureTimezoneId();

		this.popup_target_id = entity_id;
		this.popup_type = entity_type;

		if (!isDefined(entity_id) || !isDefined(entity_type)) {
			this.reloading = false;
			return;
		}

		this.popup = {
			organization_id: { $oid: this.organizationId },
			name: 'New Pop Up',
			pop_up_type: entity_type,
			action: { type: 'none' },
			is_active: true,
			header: 'Check out this pop up!',
			description: 'This is a description for the pop up. You can edit this to provide more details.',
			background_color: '#ffffff',
			text_color: '#000000',
			cta_background_color: '#007bff',
			cta_text_color: '#ffffff',
			display_when_embedded: true,
			display_delay_seconds: 0,
			active_from: null,
			active_until: null,
			geo_trigger: null,
			priority: 50,
			recurrence: 'once',
			topic_ids: this.popup_target_id ? [this.popup_target_id] : [],
			header_logo_url: undefined,
			show_distance_to_user: false,
			show_point_actions: false,
			show_rating_field_id: undefined,
		};

		this.reloading = false;
	}

    renderPreview() {
        return html`
			<div class="preview-container">
				<div class="preview-header">
					<form-button
						size="md"
						variant="text"
						icon="arrow_back"
						@click=${async () => await this.exitPreviewMode()}
					>Back</form-button>
					<h2>Popup Preview</h2>
					<div></div>
				</div>
				<div class="preview-content">
					<email-capture-modal
						id="emailCapturePreview"
						.popUp=${this.popup as any}
						.userLat=${this.popup?.geo_trigger?.lat}
						.userLng=${this.popup?.geo_trigger?.lng}
						@close-modal=${async () => await this.exitPreviewMode()}
					></email-capture-modal>
				</div>
			</div>
		`;
    }

	private async exitPreviewMode() {
		this.previewMode = false;
		this.requestUpdate();
		await this.updateComplete;
		this.repopulateDateTimePicker();
	}

	private repopulateDateTimePicker() {
		if (!isDefined(this.popup) || !this.dateTimeRangePicker) return;

		if (isDefined(this.popup?.active_from)) {
			const startDateTime = convertUTCToLocale(this.popup.active_from, this.timezoneId);
			(this.dateTimeRangePicker.startDateElement.value as any) = format(startDateTime, 'yyyy-MM-dd');
			(this.dateTimeRangePicker.startTimeElement.value as any) = format(startDateTime, 'HH:mm');
		}

		if (isDefined(this.popup?.active_until)) {
			const endDateTime = convertUTCToLocale(this.popup.active_until, this.timezoneId);
			(this.dateTimeRangePicker.endDateElement.value as any) = format(endDateTime, 'yyyy-MM-dd');
			(this.dateTimeRangePicker.endTimeElement.value as any) = format(endDateTime, 'HH:mm');
		}
	}

	setPopUpSetting(setting: Partial<CreatePopUpRequest>) {
		if (!isDefined(this.popup)) return;
		this.popup = { ...this.popup, ...setting };
		this.dispatchEvent(new CustomEvent('popup-updated', { detail: { popup: this.popup } }));
		this.requestUpdate();
	}

	private onLogoUrlChanged(e: CustomEvent) {
		const logoUrl = e.detail.imageUrl;
		this.setPopUpSetting({ header_logo_url: logoUrl });
	}

	private onGeofenceAddressSelected(e: CustomEvent) {
		const location: CreateLocationRequest = e.detail.value;
		if (isDefined(this.popup) && isDefined(this.popup.geo_trigger)) {
			this.setPopUpSetting({
				geo_trigger: {
					...this.popup.geo_trigger,
					lat: location.latLng?.lat ?? 0,
					lng: location.latLng?.lng ?? 0,
				},
			});
		}
	}

	private onGeofenceInputChanged(partial: Partial<any>) {
		if (isDefined(this.popup) && isDefined(this.popup.geo_trigger)) {
			this.setPopUpSetting({
				geo_trigger: {
					...this.popup.geo_trigger,
					...partial,
				},
			});
		}
	}

	private onGeofenceMapChanged(e: CustomEvent) {
		const { lat, lng, radius_meters } = e.detail;
		this.setPopUpSetting({
			geo_trigger: {
				lat,
				lng,
				radius_meters,
			},
		});
	}

	async openPreview(popup: GetPopUpResponse | CreatePopUpRequest | undefined) {
		if (!isDefined(popup)) {
			return;
		}
		this.previewMode = true;
	}

	togglePreviewPanel() {
		this.previewMode = !this.previewMode;
		this.dispatchEvent(new CustomEvent('preview-toggled', { detail: { previewMode: this.previewMode } }));
		this.requestUpdate();
	}

	render() {
        if (this.reloading) {
            return this.renderReloading();
        }

        if (this.popup_target_id && isDefined(this.popup_type) && !isDefined(this.existing_popup) && !isDefined(this.popup)) {
            return this.renderCreate();
        }

		if (!isDefined(this.popup)) {
			return html`
				<div class="no-content">
					<p>Select an existing pop up to edit or create a new one.</p>
				</div>
			`;
		}

		return html`
				<div class="section-title">Basic Information</div>
					<proxi-textfield
						outlined
						class="input__challenge-title"
						id="popup-name"
						type="text"
						label="Popup Name"
						placeholder="e.g., Welcome Popup"
						value="${this.popup?.name}"
						@change="${(e: Event) => {
							const target = e.target as HTMLInputElement;
							this.setPopUpSetting({ name: target.value });
						}}"
					> </proxi-textfield>
					<proxi-textfield
						outlined
						class="input__priority"
						id="popup-priority"
						type="number"
						label="Priority (0-100, higher shows first)"
						value="${this.popup?.priority}"
						@change="${(e: Event) => {
							const target = e.target as HTMLInputElement;
							this.setPopUpSetting({ priority: parseInt(target.value, 10) || 50 });
						}}"
					> </proxi-textfield>
				<div class="section-title">Content</div>
					<proxi-textfield
						outlined
						class="input__challenge-title"
						id="popup-header"
						type="text"
						label="Header"
						placeholder="Popup title (max 100 chars)"
						value="${this.popup?.header}"
						@change="${(e: Event) => {
							const target = e.target as HTMLInputElement;
							this.setPopUpSetting({ header: target.value });
						}}"
					> </proxi-textfield>
					<proxi-textarea
						outlined
						class="input__challenge-title"
						id="popup-description"
						type="text"
						label="Description"
						placeholder="Popup description (max 500 chars)"
						value="${this.popup?.description}"
						@change="${(e: Event) => {
							const target = e.target as HTMLTextAreaElement;
							this.setPopUpSetting({ description: target.value });
						}}"
					> </proxi-textarea>
				<div class="section-title">Styling</div>
					<div class="form-group">
						<label>Header Cover Image</label>
						${undefinedIfEmpty(this.popup?.header_logo_url) ? html`
							<div class="image-preview-container">
								<img src="${ifDefined(this.popup.header_logo_url)}" class="image-preview" />
								<mwc-icon-button
									class="image-preview-remove"
									icon="close"
									@click="${() => this.setPopUpSetting({ header_logo_url: undefined })}"
								></mwc-icon-button>
							</div>
						` : html`
							<image-upload-component
								id="logoUpload"
								.imageUrl="${this.popup?.header_logo_url ?? ''}"
								.autoUpload="${true}"
								@changed-image-url="${this.onLogoUrlChanged}"
								@retreived-autoupload-url="${this.onLogoUrlChanged}"
							></image-upload-component>
						`}
					</div>
					<color-picker
						id="backgroundColorElement"
						class="input"
						.open=${true}
						@change=${(e: CustomEvent) => {
							const backgroundColor = e.detail.value;
							this.setPopUpSetting({ background_color: backgroundColor });
						}}
						.value="${this.popup?.background_color}"
						label="Background Color"
						helper="Select the background color for the popup.">
					</color-picker>
					<color-picker
						id="textColorElement"
						class="input"
						.open=${true}
						@change=${(e: CustomEvent) => {
							const textColor = e.detail.value;
							this.setPopUpSetting({ text_color: textColor });
						}}
						value="${this.popup?.text_color}"
						label="Text Color">
					</color-picker>
					<color-picker
						id="ctaBackgroundColorElement"
						class="input"
						.open=${true}
						@change=${(e: CustomEvent) => {
							const ctaBackgroundColor = e.detail.value;
							this.setPopUpSetting({ cta_background_color: ctaBackgroundColor });
						}}
						value="${this.popup?.cta_background_color}"
						label="CTA Button Background Color">
					</color-picker>
					<color-picker
						id="ctaTextColorElement"
						class="input"
						.open=${true}
						@change=${(e: CustomEvent) => {
							const ctaTextColor = e.detail.value;
							this.setPopUpSetting({ cta_text_color: ctaTextColor });
						}}
						value="${this.popup?.cta_text_color}"
						label="CTA Button Text Color">
					</color-picker>

					<div class="section-title">What should happen next?</div>
					<div class="action-guidance">
					<p class="guidance-intro">Choose what users will do when they interact with your popup:</p>
					<div class="action-cards">
						<div class="action-card" ?selected=${this.selectedActionType === 'None'} @click=${() => this.handleActionTypeChange('None')}>
							<div class="action-card-icon">ℹ️</div>
							<div class="action-card-content">
								<h5>Info Only</h5>
								<p class="action-card-description">Users dismiss the popup without any action. Best for announcements and information.</p>
							</div>
						</div>
						<div class="action-card" ?selected=${this.selectedActionType === 'CollectEmail'} @click=${() => this.handleActionTypeChange('CollectEmail')}>
							<div class="action-card-icon">✉️</div>
							<div class="action-card-content">
								<h5>Collect Email</h5>
								<p class="action-card-description">Ask users to opt-in and capture their email. Great for lead generation and newsletters.</p>
							</div>
						</div>
						<div class="action-card" ?selected=${this.selectedActionType === 'LinkOut'} @click=${() => this.handleActionTypeChange('LinkOut')}>
							<div class="action-card-icon">🔗</div>
							<div class="action-card-content">
								<h5>Link Out</h5>
								<p class="action-card-description">Direct users to an external URL. Perfect for promotions, surveys, or external resources.</p>
							</div>
						</div>
						<div class="action-card" ?selected=${this.selectedActionType === 'OpenPoint'} @click=${() => this.handleActionTypeChange('OpenPoint')}>
							<div class="action-card-icon">📍</div>
							<div class="action-card-content">
								<h5>Open Point</h5>
								<p class="action-card-description">Show a specific location or point on the map. Ideal for featured locations or points of interest.</p>
							</div>
						</div>
					</div>
					<div class="action-fields">
						${this.renderActionFields()}
					</div>
				</div>

				<div class="section-title">When should users see this?</div>
					<div class="trigger-selector">
						<p class="trigger-intro">Choose one or more trigger conditions:</p>
						<div class="trigger-cards">
							<div class="trigger-card" ?selected=${this.popup?.pop_up_type === PopUpTrigger.OnMapLoad} @click=${() => this.setPopUpSetting({ pop_up_type: PopUpTrigger.OnMapLoad })}>
								<div class="trigger-card-icon">🗺️</div>
								<div class="trigger-card-content">
									<h5>On Map Load</h5>
									<p class="trigger-card-description">Show immediately when the map loads</p>
								</div>
							</div>
							<div class="trigger-card" ?selected=${this.popup?.pop_up_type === PopUpTrigger.LogicBased} @click=${() => this.setPopUpSetting({ pop_up_type: PopUpTrigger.LogicBased })}>
								<div class="trigger-card-icon">✓</div>
								<div class="trigger-card-content">
									<h5>Logic Based</h5>
									<p class="trigger-card-description">Show based on custom rules or conditions</p>
								</div>
							</div>
						</div>
					</div>

					${this.popup?.pop_up_type === PopUpTrigger.LogicBased ? html`
						<div class="logic-based-options">
							<p class="logic-based-intro">Add additional logic conditions:</p>
							<div class="sub-trigger-cards">
								<div class="sub-trigger-card" ?selected=${isDefined(this.popup?.geo_trigger)} @click=${() => {
									if (isDefined(this.popup?.geo_trigger)) {
										this.setPopUpSetting({ geo_trigger: null });
									} else {
										// Default geofence to selected point's location if Open Point action is set
										const selectedPointId = this.popup?.action.type === 'open-point' ? this.popup.action.data.point_id.$oid : null;
										const selectedPoint = isDefined(selectedPointId) ? pointCache.orderedPoints.find(p => p._id?.$oid === selectedPointId) : null;
										const defaultLat = selectedPoint?.location?.latLng?.lat ?? 0;
										const defaultLng = selectedPoint?.location?.latLng?.lng ?? 0;
										this.setPopUpSetting({ geo_trigger: { lat: defaultLat, lng: defaultLng, radius_meters: 100 } });
									}
								}}>
									<div class="sub-trigger-card-icon">📍</div>
									<div class="sub-trigger-card-content">
										<h5>Geofence Trigger</h5>
										<p class="sub-trigger-card-description">Show when users enter a location area</p>
									</div>
								</div>
								<div class="sub-trigger-card" ?selected=${isDefined(this.popup?.active_from) || isDefined(this.popup?.active_until)} @click=${() => {
									if (isDefined(this.popup?.active_from) || isDefined(this.popup?.active_until)) {
										this.setPopUpSetting({ active_from: null, active_until: null });
									} else {
										const now = new Date().toISOString();
										this.setPopUpSetting({ active_from: now });
									}
								}}>
									<div class="sub-trigger-card-icon">⏰</div>
									<div class="sub-trigger-card-content">
										<h5>Time Period</h5>
										<p class="sub-trigger-card-description">Show during a specific date/time range</p>
									</div>
								</div>
							</div>
						</div>
					` : ''}

					${isDefined(this.popup?.geo_trigger) ? html`
						<div class="conditional-section">
							<h5>Geofence Settings</h5>
							<div class="form-group">
								<label>Search Location</label>
								<proxi-input-place
									id="geofenceAddressSearch"
									placeholder="Search for an address..."
									@value-changed="${this.onGeofenceAddressSelected}"
								></proxi-input-place>
							</div>
							<div class="two-column">
								<proxi-textfield
									outlined
									type="number"
									label="Latitude"
									value="${this.popup?.geo_trigger?.lat ?? topicData.topic?.default_map_center.latLng.lat ?? ''}"
									@change="${(e: Event) => {
										const target = e.target as HTMLInputElement;
										this.onGeofenceInputChanged({ lat: parseFloat(target.value) });
									}}"
								></proxi-textfield>
								<proxi-textfield
									outlined
									type="number"
									label="Longitude"
									value="${this.popup?.geo_trigger?.lng ?? topicData.topic?.default_map_center.latLng.lng ?? ''}"
									@change="${(e: Event) => {
										const target = e.target as HTMLInputElement;
										this.onGeofenceInputChanged({ lng: parseFloat(target.value) });
									}}"
								></proxi-textfield>
							</div>
							<proxi-textfield
								outlined
								type="number"
								label="Radius (meters)"
								value="${this.popup?.geo_trigger?.radius_meters ?? 100}"
								@change="${(e: Event) => {
									const target = e.target as HTMLInputElement;
									this.onGeofenceInputChanged({ radius_meters: parseInt(target.value, 10) });
								}}"
							></proxi-textfield>
							<geofence-map-picker
								.lat="${this.popup?.geo_trigger?.lat ?? 0}"
								.lng="${this.popup?.geo_trigger?.lng ?? 0}"
								.radiusMeters="${this.popup?.geo_trigger?.radius_meters ?? 100}"
								@geofence-changed="${this.onGeofenceMapChanged}"
							></geofence-map-picker>
							<div class="checkbox-wrapper" style="margin-top: 12px;">
								<form-checkbox
									id="showDistanceToUser"
									name="showDistanceToUser"
									class_name="blue"
									?checked="${this.popup?.show_distance_to_user}"
									@change="${(e: Event) => {
										const target = e.target as HTMLInputElement;
										this.setPopUpSetting({ show_distance_to_user: target.checked });
									}}"
								></form-checkbox>
								<span>Show distance to user</span>
							</div>
						</div>
					` : ''}

					${this.popup?.active_from != null || this.popup?.active_until != null ? html`
						<div class="conditional-section">
							<h5>Time Period Settings</h5>
							<div class="date-container">
								<date-time-range-picker
									id="dateTimeRangePicker"
									class=""
									.timeOnly=${false}
									.dateOnly=${false}
									.timezoneShorthand=${this.timezoneShortName ?? ''}
									.showEndDateTime=${true}
									.showEndDateTimeToggle=${false}
									.allDay=${false}
									.hideAllDayToggle=${true}
									.startDateTime=${this.popup?.active_from ?? ''}
									.endDateTime=${this.popup?.active_until ?? ''}
									@change="${this.updatePopupScheduledTime}"
									>
								</date-time-range-picker>
							</div>
						</div>
					` : ''}

				<div class="section-title">Display Settings</div>
					<div class="checkbox-wrapper">
						<form-checkbox
							id="displayWhenEmbedded"
							name="displayWhenEmbedded"
							class_name="blue"
							?checked="${this.popup?.display_when_embedded}"
							@change="${(e: Event) => {
								const target = e.target as HTMLInputElement;
								this.setPopUpSetting({ display_when_embedded: target.checked });
							}}"
						></form-checkbox>
						<span>Display when embedded</span>
					</div>
					<proxi-textfield
						outlined
						class="input__display-delay"
						id="displayDelay"
						type="number"
						label="Display Delay (seconds)"
						value="${this.popup?.display_delay_seconds}"
						@change="${(e: Event) => {
							const target = e.target as HTMLInputElement;
							this.setPopUpSetting({ display_delay_seconds: parseInt(target.value, 10) || 0 });
						}}"
					> </proxi-textfield>
					<proxi-selectfield
						outlined
						id="recurrence"
						label="Display Frequency"
						value=${this.popup?.recurrence ?? 'once'}
						@selected="${(e: Event) => {
							const target = e.target as HTMLInputElement;
							this.setPopUpSetting({ recurrence: target.value as 'once' | 'daily' | 'weekly' | 'monthly' });
						}}"
					>
						<mwc-list-item value="once">Once Ever</mwc-list-item>
						<mwc-list-item value="daily">Once a Day</mwc-list-item>
						<mwc-list-item value="weekly">Once a Week</mwc-list-item>
						<mwc-list-item value="monthly">Once a Month</mwc-list-item>
					</proxi-selectfield>

				 	<div class="button__wrapper">
					<div style="display: flex; align-items: center; gap: 10px; justify-content: flex-start;flex-direction: row;">
						${isDefined(this.existing_popup) ? html`
							<form-button
								class="challeng-save-button"
								@click="${this.handleClose}"
								size="sm"
							>
								Close
							</form-button>
						` : ''}
					</div>
					<div style="display: flex; gap: 10px;">
						<form-button
							style="flex: 1;"
							?disabled="${!this.featureEnabled}"
							class="challeng-save-button"
							@click="${async () => await this.handleSaveAsDraft()}"
							size="sm"
							variant="secondary"
						>
							${this.getDraftButtonLabel()}
						</form-button>
						<form-button
							style="flex: 1;"
							?disabled="${!this.featureEnabled}"
							class="challeng-save-button"
							@click="${async () => await this.handleSaveAndSetLive()}"
							size="sm"
						>
							${this.getLiveButtonLabel()}
						</form-button>
					</div>
				</div>
		`;
	}


	static styles = css`
		:host {
			display: block;
		}

		.form-group {
			margin-bottom: 20px;
		}

		.image-preview-container {
			position: relative;
			display: inline-block;
			margin-top: 8px;
		}

		.image-preview {
			max-width: 100%;
			max-height: 120px;
			border-radius: 8px;
			border: 1px solid var(--theme-input-border-color);
		}

		.image-preview-remove {
			position: absolute;
			top: -8px;
			right: -8px;
			background: var(--main-moon-color);
			border-radius: 50%;
			--mdc-icon-button-size: 24px;
			--mdc-icon-size: 16px;
		}

		label {
			display: block;
			margin-bottom: 8px;
			font-weight: 500;
		}

		input[type="text"],
		input[type="number"],
		input[type="date"],
		input[type="time"],
		select,
		textarea {
			width: 100%;
			padding: 8px;
			border: 1px solid #ccc;
			border-radius: 4px;
			font-family: inherit;
			font-size: inherit;
		}

		textarea {
			resize: vertical;
			min-height: 100px;
		}

		.color-input-group {
			display: flex;
			gap: 10px;
			align-items: center;
		}

		input[type="color"] {
			width: 50px;
			height: 50px;
			border: none;
			border-radius: 4px;
			cursor: pointer;
		}

		.color-label {
			flex: 1;
		}

		.action-selector {
			border: 1px solid #ddd;
			border-radius: 4px;
			padding: 12px;
			margin-bottom: 15px;
		}

		.action-option {
			margin-bottom: 10px;
		}

		.action-option input[type="radio"] {
			margin-right: 8px;
		}

		.button__wrapper {
			width: 100%;
			display: flex;
			align-items: flex-start;
			justify-content: space-between;
			margin: 15px 0;
		}

		.action-fields {
			margin-left: 24px;
			margin-top: 20px;
			padding-left: 12px;
			border-left: 2px solid #ddd;
		}

		.two-column {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 15px;
		}

		.buttons {
			display: flex;
			gap: 10px;
			margin-top: 20px;
		}

		button {
			padding: 10px 20px;
			border: none;
			border-radius: 4px;
			font-size: 14px;
			cursor: pointer;
			font-weight: 500;
		}

		.btn-primary {
			background-color: #007bff;
			color: white;
		}

		.btn-primary:hover {
			background-color: #0056b3;
		}

		.btn-secondary {
			background-color: #6c757d;
			color: white;
		}

		.btn-secondary:hover {
			background-color: #5a6268;
		}

		.checkbox-wrapper {
			width: 100%;
			display: flex;
			align-items: center;
			justify-content: flex-start;
			gap: 8px;
			line-height: 120%;
			color: #102C3D;
		}

		.error {
			color: #d32f2f;
			font-size: 12px;
			margin-top: 4px;
		}

		.topic-selector {
			max-height: 200px;
			overflow-y: auto;
			border: 1px solid #ccc;
			border-radius: 4px;
			padding: 8px;
		}

		.topic-checkbox {
			display: block;
			margin-bottom: 8px;
		}

		.section-title {
			font-size: 16px;
			font-weight: 600;
			margin-top: 20px;
			margin-bottom: 12px;
			border-bottom: 2px solid #e0e0e0;
			padding-bottom: 8px;
		}

		.action-guidance {
			margin-bottom: 20px;
		}

		.guidance-intro {
			color: #666;
			font-size: 14px;
			margin: 0 0 15px 0;
			font-weight: 500;
		}

		.action-cards {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 12px;
		}

		.action-card {
			display: flex;
			gap: 12px;
			padding: 12px;
			border: 2px solid #e0e0e0;
			border-radius: 6px;
			background-color: #fafafa;
			transition: all 0.2s ease;
			cursor: pointer;
		}

		.action-card:hover {
			border-color: #007bff;
			background-color: #f0f7ff;
		}

		.action-card[selected] {
			border-color: #007bff;
			background-color: #f0f7ff;
			box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
		}

		.action-card-icon {
			font-size: 24px;
			flex-shrink: 0;
		}

		.action-card-content {
			flex: 1;
		}

		.action-card-content h5 {
			margin: 0 0 4px 0;
			font-size: 14px;
			font-weight: 600;
			color: #333;
		}

		.action-card-description {
			margin: 0;
			font-size: 12px;
			color: #666;
			line-height: 1.4;
		}

		@media (max-width: 600px) {
			.action-cards {
				grid-template-columns: 1fr;
			}
		}

		.trigger-selector {
			margin-bottom: 20px;
		}

		.trigger-intro {
			color: #666;
			font-size: 14px;
			margin: 0 0 15px 0;
			font-weight: 500;
		}

		.trigger-cards {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
			gap: 12px;
		}

		.trigger-card {
			display: flex;
			gap: 12px;
			padding: 12px;
			border: 2px solid #e0e0e0;
			border-radius: 6px;
			background-color: #fafafa;
			transition: all 0.2s ease;
			cursor: pointer;
		}

		.trigger-card:hover {
			border-color: #007bff;
			background-color: #f0f7ff;
		}

		.trigger-card[selected] {
			border-color: #007bff;
			background-color: #f0f7ff;
			box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
		}

		.trigger-card-icon {
			font-size: 24px;
			flex-shrink: 0;
		}

		.trigger-card-content {
			flex: 1;
		}

		.trigger-card-content h5 {
			margin: 0 0 4px 0;
			font-size: 14px;
			font-weight: 600;
			color: #333;
		}

		.trigger-card-description {
			margin: 0;
			font-size: 12px;
			color: #666;
			line-height: 1.4;
		}

		.conditional-section {
			background-color: #f9f9f9;
			border-left: 4px solid #007bff;
			padding: 15px;
			margin-bottom: 15px;
			border-radius: 4px;
		}

		.conditional-section h5 {
			margin-top: 0;
			margin-bottom: 12px;
			color: #333;
			font-size: 14px;
			font-weight: 600;
		}

		.conditional-section proxi-textfield {
			margin-bottom: 12px;
		}

		.date-container {
			margin-top: 10px;
			margin-bottom: 15px;
		}

		.logo-upload-container {
			margin-top: 8px;
		}

		.logo-preview {
			display: flex;
			align-items: center;
			gap: 10px;
			padding: 10px;
			background-color: #f5f5f5;
			border-radius: 4px;
		}

		.logo-preview img {
			max-height: 100px;
			max-width: 200px;
			border-radius: 4px;
		}

		.logic-based-options {
			margin-top: 20px;
			padding: 15px;
			background-color: #f5f5f5;
			border-left: 4px solid #007bff;
			border-radius: 4px;
			margin-bottom: 15px;
		}

		.logic-based-intro {
			color: #666;
			font-size: 13px;
			margin: 0 0 12px 0;
			font-weight: 500;
		}

		.sub-trigger-cards {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
			gap: 10px;
		}

		.sub-trigger-card {
			display: flex;
			gap: 10px;
			padding: 10px;
			border: 2px solid #d0d0d0;
			border-radius: 4px;
			background-color: #ffffff;
			transition: all 0.2s ease;
			cursor: pointer;
		}

		.sub-trigger-card:hover {
			border-color: #007bff;
			background-color: #f0f7ff;
		}

		.sub-trigger-card[selected] {
			border-color: #007bff;
			background-color: #e3f2fd;
			box-shadow: 0 1px 4px rgba(0, 123, 255, 0.15);
		}

		.sub-trigger-card-icon {
			font-size: 18px;
			flex-shrink: 0;
		}

		.sub-trigger-card-content {
			flex: 1;
		}

		.sub-trigger-card-content h5 {
			margin: 0 0 2px 0;
			font-size: 12px;
			font-weight: 600;
			color: #333;
		}

		.sub-trigger-card-description {
			margin: 0;
			font-size: 10px;
			color: #888;
			line-height: 1.3;
		}

		.preview-container {
			display: flex;
			flex-direction: column;
			height: 100%;
			width: 100%;
		}

		.preview-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
			padding: 15px 20px;
			border-bottom: 1px solid #e0e0e0;
			background-color: #fafafa;
		}

		.preview-header h2 {
			margin: 0;
			flex: 1;
			text-align: center;
			font-size: 18px;
			font-weight: 600;
			color: #333;
		}

		.preview-header > div:first-child {
			flex: 1;
		}

		.preview-header > div:last-child {
			flex: 1;
		}

		.preview-content {
			flex: 1;
			overflow-y: auto;
			padding: 20px;
			background-color: #f5f5f5;
			display: flex;
			align-items: center;
			justify-content: center;
			border-radius: 4px;
		}

		email-capture-modal {
			max-width: 600px;
			width: 100%;
		}

		@media (max-width: 768px) {
			.trigger-cards {
				grid-template-columns: 1fr 1fr;
			}
		}

		@media (max-width: 600px) {
			.trigger-cards {
				grid-template-columns: 1fr;
			}
		}
	`;
}
